import React from 'react'
import styled from 'styled-components'
import { PictureAsPdfOutlined } from '@mui/icons-material'

import { Panel } from 'components/Panel'
import Spinner from 'components/Spinner'
import Table from 'components/Table'

import { shortDateTime } from 'helpers/formatters'
import Apptentive from 'helpers/apptentive'
import Brand from 'helpers/brand'
import GA4 from 'helpers/ga4'

import { useStoreContext } from 'hooks/StoreContext'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

const VerificationReportsPanel = () => {
  const { verificationReports, areVerificationReportsLoaded } = useStoreContext()

  const onClickReport = () => {
    GA4.sendEvent({
      category: 'Customer Detail Page',
      action: 'Click PDF Report',
      label: 'Report Type: Link Verification'
    })

    Apptentive.sendEvent('verification_pdf_report_viewed')
  }

  const parseTimestampToDate = (timestamp: string): Date => {
    return new Date(timestamp.replace(/\s*\|\s*/, ' '))
  }

  const SortByTimeStamp = (Reports) => {
    return Reports.sort((a, b) => {
      const dateA = parseTimestampToDate(a.metadata.timestamp).valueOf()
      const dateB = parseTimestampToDate(b.metadata.timestamp).valueOf()

      return dateB - dateA
    })
  }

  return (
    <S.Panel id="link-verification-reports" scrollToSection>
      <Panel.Header>
        <Panel.Title>Link Verification Reports</Panel.Title>
      </Panel.Header>

      {!areVerificationReportsLoaded && <Spinner variant="page" />}

      <>
        {areVerificationReportsLoaded && (
          <Table
            items={verificationReports || []}
            noDataMessage={
              {
                title: 'NO AVAILABLE REPORTS',
                description: `When connected to a Link System over Bluetooth, generate a Verification Report from the ${Brand.brandName()} Technician App to see report here.`
              }
            }
          >
            <Table.Header>
              <Table.HeaderCell title="Name" />
              <Table.HeaderCell title="Created" />
            </Table.Header>
            {SortByTimeStamp(verificationReports).map((report) => (
              <Table.Row key={report.s3FileName}>
                <S.Cell>
                  <S.ReportFileLink
                    onClick={onClickReport}
                    href={report.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`View report: ${report.s3FileName}`}
                  >
                    <S.PdfIcon sx={{ fontSize: 40 }} />

                    <div>{report.s3FileName}</div>
                  </S.ReportFileLink>
                </S.Cell>
                <S.Cell><S.ReportTimeStamp>{shortDateTime(parseTimestampToDate(report.metadata.timestamp))}</S.ReportTimeStamp></S.Cell>
              </Table.Row>
            ))}
          </Table>
        )}
      </>
    </S.Panel>
  )
}

export default VerificationReportsPanel

const S = {
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
    background-color: ${color('paneBackground')};
  `,
  ReportFileLink: styled.a`
    margin: 15px 0;
    display: flex;

    @media ${screenSize.small} {
      margin: 0;
    }
  `,
  ReportTimeStamp: styled.div`
    margin: 15px 0;
    display: flex;

    @media ${screenSize.small} {
      margin: 0 0 0 35px;
    }
  `,
  Cell: styled(Table.Cell)`
    padding-left: 15px;

    @media ${screenSize.small} {
      padding-left: 0;
    }
  `,
  PdfIcon: styled(PictureAsPdfOutlined)`
    margin-right: 10px;
    font-size: 24px;
  `
}
